'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { billingListMachine } from './machines/billingListMachine'
import type { BillingAccount, PaymentHistory, ChargeHistory } from './types'

export type BillingListMachineApi = ReturnType<typeof useMachine<typeof billingListMachine>>

const BillingListMachineContext = createContext<BillingListMachineApi | null>(null)

export function BillingListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(billingListMachine)
  return (
    <BillingListMachineContext.Provider value={api}>{children}</BillingListMachineContext.Provider>
  )
}

export function useBillingListMachine() {
  const ctx = useContext(BillingListMachineContext)
  if (!ctx) {
    throw new Error('useBillingListMachine must be used within BillingListMachineProvider')
  }
  return ctx
}

export function useBillingListMachineOptional() {
  return useContext(BillingListMachineContext)
}

export function useBillingListStoreCompat() {
  const machineApi = useBillingListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context
    return {
      accounts: ctx.accounts,
      paymentHistory: ctx.paymentHistory,
      chargeHistory: ctx.chargeHistory,
      searchResults: ctx.searchResults,
      isLoading: ctx.isLoading,
      error: ctx.error,
      setAccount: (account: BillingAccount) => send({ type: 'SET_ACCOUNT', account }),
      setAccounts: (accounts: BillingAccount[]) => send({ type: 'SET_ACCOUNTS', accounts }),
      setPaymentHistory: (accountNumber: string, history: PaymentHistory[]) =>
        send({ type: 'SET_PAYMENT_HISTORY', accountNumber, history }),
      setChargeHistory: (accountNumber: string, history: ChargeHistory[]) =>
        send({ type: 'SET_CHARGE_HISTORY', accountNumber, history }),
      setSearchResults: (results: BillingAccount[]) =>
        send({ type: 'SET_SEARCH_RESULTS', results }),
      setLoading: (isLoading: boolean) => send({ type: 'SET_LOADING', isLoading }),
      setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
      clearSearchResults: () => send({ type: 'CLEAR_SEARCH_RESULTS' }),
      getAccount: (accountNumber: string) => ctx.accounts.get(accountNumber),
      getPaymentHistory: (accountNumber: string) => ctx.paymentHistory.get(accountNumber) || [],
      getChargeHistory: (accountNumber: string) => ctx.chargeHistory.get(accountNumber) || [],
    }
  }, [machineApi])
}

export function useCrmBillingActions() {
  const machineApi = useBillingListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setAccount: (account: BillingAccount) => send({ type: 'SET_ACCOUNT', account }),
    setAccounts: (accounts: BillingAccount[]) => send({ type: 'SET_ACCOUNTS', accounts }),
    setPaymentHistory: (accountNumber: string, history: PaymentHistory[]) =>
      send({ type: 'SET_PAYMENT_HISTORY', accountNumber, history }),
    setChargeHistory: (accountNumber: string, history: ChargeHistory[]) =>
      send({ type: 'SET_CHARGE_HISTORY', accountNumber, history }),
    setSearchResults: (results: BillingAccount[]) =>
      send({ type: 'SET_SEARCH_RESULTS', results }),
    setLoading: (isLoading: boolean) => send({ type: 'SET_LOADING', isLoading }),
    setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
    clearSearchResults: () => send({ type: 'CLEAR_SEARCH_RESULTS' }),
  }), [send])
}

export function useAccountWithDetails(accountNumber: string | null) {
  const compat = useBillingListStoreCompat()
  return useMemo(
    () => (accountNumber ? compat.accounts.get(accountNumber) : undefined),
    [compat.accounts, accountNumber]
  )
}

export function useHasActiveServices(accountNumber: string | null) {
  const account = useAccountWithDetails(accountNumber)
  return useMemo(() => {
    if (!account) return false
    return account.services.some((s) => s.status === 'active')
  }, [account])
}

export function useTotalMonthlyFees(accountNumber: string | null) {
  const account = useAccountWithDetails(accountNumber)
  return useMemo(() => {
    if (!account) return 0
    const servicesFees = account.services
      .filter((s) => s.status === 'active')
      .reduce((sum, s) => sum + s.monthlyFee, 0)
    const equipmentFees = account.equipment
      .filter((eq) => eq.status === 'issued' && eq.rentalFee)
      .reduce((sum, eq) => sum + (eq.rentalFee || 0), 0)
    return servicesFees + equipmentFees
  }, [account])
}
